<template>
    <div
        ref="containerRegionChart"
        id="containerRegionChart"
        class="dashboard-summary-chart pl-2 pr-2"

    >
        <svg :height="svg.height" :width="svg.width">
            <circle :cx="svg.width/2" :cy="svg.height/2" :r="svg.width/2"  :fill="themeColors.light" opacity="0.8"></circle>
            <g v-for="(node, nodeIdx) in regionsWithCost" :key="nodeIdx" :transform="'translate('+ svg.width/2 + ', ' + svg.height/2 + ') rotate(45)'">
                <path
                    style="cursor:pointer"
                    :fill="nodeIdx < 1 ? themeColors.warning : themeColors.primary"
                    :d="d(node)"
                    @click="emitDetailLayer(node.region)"
                >
                </path>
                <g text-anchor="start"
                    :transform="'rotate(' + ((x(node.region) + x.bandwidth() / 2) * 180 / Math.PI - 90) + ') translate(' + (circle.inner + 10) + ', 0)'">
                    <text
                        style="cursor:pointer"
                        y="-8"
                        text-anchor="start"
                        alignment-baseline="middle"
                        :font-size="Math.max(Math.min(svg.width/30, 15), 12)"
                        :fill="nodeIdx == 0 ? themeColors.light : themeColors.dark"
                        font-weight="bold"
                        v-if="(x(node.region) + x.bandwidth() / 2) * 180 / Math.PI - 145 < 0 || (x(node.region) + x.bandwidth() / 2) * 180 / Math.PI - 145 > 180"
                        @click="emitDetailLayer(node.region)"
                    >
                        {{ node.name }}
                    </text>
                    <text
                        style="cursor:pointer"
                        y="8"
                        text-anchor="start"
                        alignment-baseline="middle"
                        :font-size="Math.max(Math.min(svg.width/30, 15), 12)"
                        :fill="nodeIdx == 0 ? themeColors.light : themeColors.dark"
                        v-if="(x(node.region) + x.bandwidth() / 2) * 180 / Math.PI - 145 < 0 || (x(node.region) + x.bandwidth() / 2) * 180 / Math.PI - 145 > 180"
                        @click="emitDetailLayer(node.region)"
                    >
                        {{ $_getDollar(node.cost).replace(' ','') }}
                    </text>
                    <text
                        style="cursor:pointer"
                        y="-8"
                        text-anchor="end"
                        font-weight="bold"
                        alignment-baseline="middle"
                        :font-size="Math.max(Math.min(svg.width/30, 15), 12)"
                        :fill="nodeIdx == 0 ? themeColors.light : themeColors.dark"
                        v-if="(x(node.region) + x.bandwidth() / 2) * 180 / Math.PI - 145 >= 0 && (x(node.region) + x.bandwidth() / 2) * 180 / Math.PI - 145 < 180"
                        transform="rotate(180)"
                        @click="emitDetailLayer(node.region)"
                    >
                        {{ node.name }}
                    </text>
                    <text
                        style="cursor:pointer"
                        y="8"
                        text-anchor="end"
                        alignment-baseline="middle"
                        :font-size="Math.max(Math.min(svg.width/30, 15), 12)"
                        :fill="nodeIdx == 0 ? themeColors.light : themeColors.dark"
                        v-if="(x(node.region) + x.bandwidth() / 2) * 180 / Math.PI - 145 >= 0 && (x(node.region) + x.bandwidth() / 2) * 180 / Math.PI - 145 < 180"
                        transform="rotate(180)"
                        @click="emitDetailLayer(node.region)"
                    >
                        {{ $_getDollar(node.cost).replace(' ','') }}
                    </text>
                </g>
            </g>
        </svg>
        <b-alert class="mt-0 mb-0 p-1"
            :variant="theme"
            show
        >
            <p class="float-right" v-if="bottomCards.length > 1">
                <a @click="showNextCard">
                    <i class="fas fa-chevron-circle-right"></i>
                </a>
            </p>
            <h6 v-if="bottomCardItem" class="text-nowrap" v-html="bottomCardItem.title"></h6>
            <p v-if="bottomCardItem" class="mt-1 mb-0 text-nowrap" style="line-height:14px;">
                <small class="font-weight-bold font-italic">
                    {{ bottomCardItem.message }}
                </small>
            </p>
        </b-alert>
    </div>
</template>

<script>
import useCommonData from '@/composables/useCommonData'
import { computed, inject, getCurrentInstance, ref, onMounted, onBeforeUnmount, toRefs, watch, nextTick } from '@vue/composition-api'
export default {
    props: {
        svg: {
            type: Object,
            default: function () {
                return {}
            }
        },
        thisMonthRegions: {
            type: Array,
            default: function () {
                return []
            }
        },
        lastMonthRegions: {
            type: Array,
            default: function () {
                return []
            }
        }
    },
    setup (props, context) {
        const vm = getCurrentInstance().proxy
        const { thisMonthRegions, lastMonthRegions, svg } = toRefs(props)
        const themeColors = inject('themeColors')
        const theme = inject('theme')
        const { region, __getRegionNameByKey } = useCommonData()
        const d3 = vm.$d3
        const containerRegionChart = ref(null)
        const cardToShow = ref(0)
        const timeoutId = ref(null)
        const data = computed(() => ({
            thisMonthRegions: thisMonthRegions.value,
            lastMonthRegions: lastMonthRegions.value
        }))
        const maxCost = computed(() => Math.max.apply(null, thisMonthRegions.value.map(item => item.cost)))
        const totalCost = computed(() => thisMonthRegions.value.map(item => item.cost).reduce((a, b) => a + b), 0)
        const lastMonthTotalCost = computed(() => lastMonthRegions.value.map(item => item.cost).reduce((a, b) => a + b, 0))
        const allRegionsWithCost = computed(() => {
            return region.value.map(item => {
                const findItem = thisMonthRegions.value.find(region => region.key === item.key)
                return {
                    region: item.key,
                    name: item.name,
                    cost: (findItem ? findItem.cost : 0)
                }
            }).sort((a, b) => {
                return a.cost > b.cost ? -1 : 1
            })
        })
        const regionsWithCost = computed(() => {
            return allRegionsWithCost.value.filter(item => item.cost > 0)
                .map((item, itemIdx) => {
                    return itemIdx < 7 ? item : {
                        region: 'etc',
                        name: 'Etc',
                        cost: item.cost
                    }
                }).reduce((acc, val) => {
                    const findItem = acc.find(item => item.region === val.region)
                    if (findItem) {
                        findItem.cost += val.cost
                    } else {
                        acc.push(val)
                    }
                    return acc
                }, [])
        })
        const computedRegionCost = computed(() => {
            return thisMonthRegions.value.map(tm => {
                const lastMonthCost = lastMonthRegions.value.find(lm => lm.key === tm.key)?.cost || 0
                return {
                    key: tm.key,
                    cost: tm.cost,
                    costIncrease: tm.cost - lastMonthRegions.value.find(lm => lm.key === tm.key)?.cost || 0,
                    percentageIncrease: (tm.cost / totalCost.value) - (lastMonthCost / lastMonthTotalCost.value),
                    equal: (tm.cost / totalCost.value) === (lastMonthCost / lastMonthTotalCost.value)
                }
            })
        })
        const regionCostRising = computed(() => {
            return computedRegionCost.value.sort((a, b) => a.costIncrease > b.costIncrease ? -1 : 1)
                .filter((item, itemIdx) => item.costIncrease > 0 && itemIdx < 1)
                .map(item => {
                    return {
                        title: __getRegionNameByKey(item.key) + ' <small>' + vm.$_getDollar(item.costIncrease).replace(' ', '') + ' <i class="fas fa-caret-up"></i></small>',
                        message: vm.$t('v4.highest cost increasing')
                    }
                })
        })
        const regionPercentageRising = computed(() => {
            return computedRegionCost.value.sort((a, b) => a.percentageIncrease > b.percentageIncrease ? -1 : 1)
                .filter((item, itemIdx) => item.percentageIncrease > 0 && itemIdx < 1)
                .map(item => {
                    return {
                        title: __getRegionNameByKey(item.key) + ' <small>' + (Math.round(item.percentageIncrease * 100 * 100) / 100) + '% <i class="fas fa-caret-up"></i></small>',
                        message: vm.$t('v4.highest percentage increasing')
                    }
                })
        })
        const newRegionsThisMonth = computed(() => {
            const tmp = thisMonthRegions.value.filter(tm => {
                return !lastMonthRegions.value.filter(lm => lm.key === tm.key).length
            }).map(item => item.key)
            return tmp.length ? {
                title: tmp.length < 3 ? tmp.map(item => __getRegionNameByKey(item)).join(', ') : __getRegionNameByKey(tmp[0]) + ' <small> & ' + (tmp.length - 1) + ' more regions</small>',
                message: vm.$t('v4.new regions of the month')
            } : null
        })
        const regionPercentageSame = computed(() => {
            return !regionCostRising.value.length && !regionPercentageRising.value.length && computedRegionCost.value.sort((a, b) => a.cost > b.cost ? -1 : 1).every(item => item.equal) ? [
                {
                    title: __getRegionNameByKey(computedRegionCost.value[0].key) + `<small> & ${computedRegionCost.value.length - 1} more regions</small>`,
                    message: vm.$t('v4.same percentage')
                }
            ] : []
        })
        // const bottomCards = computed(() => [regionCostRising.value, regionPercentageRising.value, regionPercentageSame.value, newRegionsThisMonth.value].filter(item => item !== null).reduce((acc, val) => acc.concat(val), []))
        const bottomCards = computed(() => [regionCostRising.value, regionPercentageSame.value, newRegionsThisMonth.value].filter(item => item !== null).reduce((acc, val) => acc.concat(val), []))
        const bottomCardItem = computed(() => bottomCards.value[cardToShow.value])
        const circle = computed(() => {
            return {
                inner: svg.value.width / 10,
                outer: svg.value.width / 2
            }
        })
        const x = computed(() => d3.scaleBand().range([0, Math.PI * 2]).align(0).domain(regionsWithCost.value.map(item => item.region)))
        const scaleRadial = () => {
            const linear = d3.scaleLinear()

            function scale (x) {
                return Math.sqrt(linear(x))
            }

            scale.domain = function (_) {
                return arguments.length ? (linear.domain(_), scale) : linear.domain()
            }

            scale.nice = function (count) {
                return (linear.nice(count), scale)
            }

            scale.range = function (_) {
                return arguments.length ? (linear.range(_.map(x => x * x)), scale) : linear.range().map(Math.sqrt)
            }

            scale.ticks = linear.ticks
            scale.tickFormat = linear.tickFormat

            return scale
        }
        const y = computed(() => scaleRadial().range([circle.value.inner, circle.value.outer]).domain([0, maxCost.value]))
        const d = (d) => {
            return d3.arc()
                .innerRadius(circle.value.inner)
                .outerRadius(y.value(d.cost))
                .startAngle(x.value(d.region))
                .endAngle(x.value(d.region) + x.value.bandwidth())
                .padAngle(0.05)
                .padRadius(circle.value.inner)()
        }
        const emitHandleResize = () => context.emit('resize:svg')
        const emitDetailLayer = (region) => context.emit('detail-layer', region)
        const showNextCard = () => {
            clearTimeout(timeoutId.value)
            cardToShow.value = (cardToShow.value + 1) % bottomCards.value.length
            timeoutId.value = setTimeout(showNextCard, 5000)
        }
        timeoutId.value = setTimeout(showNextCard, 5000)
        let resizeObserver = null
        onMounted(() => {
            nextTick(() => {
                resizeObserver = new ResizeObserver(() => {
                    emitHandleResize()
                })
                resizeObserver.observe(containerRegionChart.value)
            })
        })
        onBeforeUnmount(() => {
            clearTimeout(timeoutId)
            if (resizeObserver) {
                resizeObserver.disconnect()
            }
        })
        watch(data, (value) => {
            cardToShow.value = 0
        })
        return {
            containerRegionChart,
            themeColors,
            theme,
            bottomCardItem,
            regionsWithCost,
            x,
            d,
            circle,
            bottomCards,
            showNextCard,
            emitDetailLayer
        }
    }
}
</script>
